import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import '@/App.css';
import { Phone, WhatsApp, Shield, Check, CheckCircle, Clock } from './icons';
import { CitySwitcher } from './CitySwitcher';
import { matchCity, findBySlug, districtsOf } from './cities';

// Below-fold sections (services, testimonials, FAQ, contact, footer) load in a separate JS chunk.
// This keeps the initial main bundle tiny → LCP + TTI on mobile is dramatically faster.
const BelowFold = lazy(() => import('./BelowFold'));

const STORAGE_KEY = 'sr_city_slug';

function App() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeCity, setActiveCity] = useState(null); // {slug, name} | null (null = Ankara / genel)

  const cityName = activeCity ? activeCity.name : 'Ankara';
  const personalized = !!activeCity;
  const districts = districtsOf(activeCity ? activeCity.slug : '');

  // Tıklama takibi için güncel şehri ref'te tut
  const cityRef = useRef('Ankara');
  useEffect(() => { cityRef.current = cityName; }, [cityName]);

  // WhatsApp/arama tıklamalarını backend'e (sadece tıklamada, sayfa hızını etkilemez) gönder
  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL;
    if (!base) return;
    const onClick = (e) => {
      const a = e.target && e.target.closest && e.target.closest('a');
      if (!a) return;
      const h = a.getAttribute('href') || '';
      let action = null;
      if (h.indexOf('wa.me') > -1) action = 'whatsapp';
      else if (h.indexOf('tel:') === 0) action = 'call';
      if (!action) return;
      try {
        const body = JSON.stringify({ city: cityRef.current, action, source: 'home' });
        navigator.sendBeacon(`${base}/api/track`, new Blob([body], { type: 'application/json' }));
      } catch (_) {}
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, []);

  // Manuel seçim (localStorage) > IP tespiti > genel (Ankara)
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored !== null) {
      if (stored === '') { setActiveCity(null); return; }
      const c = findBySlug(stored);
      if (c) { setActiveCity(c); return; }
    }
    const base = process.env.REACT_APP_BACKEND_URL;
    if (!base) return;
    const ctrl = new AbortController();
    const run = () => {
      fetch(`${base}/api/geo`, { signal: ctrl.signal })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (!d) return;
          const c = matchCity(d.city, d.region);
          if (c) setActiveCity(c);
        })
        .catch(() => {});
    };
    // LCP'yi bloklamamak için boşta çalıştır
    const id = window.requestIdleCallback
      ? window.requestIdleCallback(run, { timeout: 2000 })
      : setTimeout(run, 800);
    return () => {
      ctrl.abort();
      if (window.cancelIdleCallback && typeof id === 'number') window.cancelIdleCallback(id);
    };
  }, []);

  // Şehir değişince sekme başlığı ve açıklamasını güncelle (UX; statik SEO etkilenmez)
  useEffect(() => {
    document.title = activeCity
      ? `${activeCity.name} İşe Giriş Sağlık Raporu | Aynı Gün Rapor · 0(553) 561 44 45`
      : 'İşe Giriş Sağlık Raporu Ankara | Aynı Gün Teslim · 0553 561 44 45';
    const meta = document.querySelector('meta[name="description"]');
    if (meta && activeCity) {
      meta.setAttribute(
        'content',
        `${activeCity.name} işe giriş sağlık raporu — Ankara Ostim & Çankaya şubelerinde aynı gün, yarım saatte teslim. Fiyat 750₺–3.000₺ (tetkik kapsamına göre). ☎ 0(553) 561 44 45`
      );
    }
  }, [activeCity]);

  const handleSelectCity = (slug, name) => {
    localStorage.setItem(STORAGE_KEY, slug);
    setActiveCity(slug ? { slug, name } : null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const waMsg = personalized
    ? `${cityName} için işe giriş sağlık raporu bilgi alabilir miyim?`
    : 'İşe giriş sağlık raporu için bilgi alabilir miyim?';
  const waHref = `https://wa.me/905535614445?text=${encodeURIComponent(waMsg)}`;

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrolled = window.scrollY > 50;
          setIsScrolled(prev => (prev !== scrolled ? scrolled : prev));
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="app-container">
      <header className={`main-header ${isScrolled ? 'scrolled' : ''}`} data-testid="main-header">
        <div className="header-container">
          <div className="logo">
            <picture>
              <source srcSet="/logo-200.webp" type="image/webp" />
              <img
                src="/logo.png"
                alt="Ankara İşe Giriş Sağlık Raporu — Klinik Logo"
                className="logo-image"
                width="100"
                height="100"
                decoding="async"
              />
            </picture>
            <div className="logo-text">
              <span className="logo-title">İşe Giriş Sağlık Raporu</span>
              <span className="logo-subtitle" data-testid="logo-subtitle">{cityName} · {districts}</span>
            </div>
          </div>

          <nav className="header-nav" aria-label="Ana navigasyon">
            <a href="#hizmetler" className="nav-link" data-testid="nav-services">Hizmetler</a>
            <a href="#kimler-almali" className="nav-link" data-testid="nav-who">Kimler Almalı</a>
            <a href="#subeler" className="nav-link" data-testid="nav-branches">Şubeler</a>
            <a href="#sss" className="nav-link" data-testid="nav-faq">S.S.S</a>
            <a href="#iletisim" className="nav-link" data-testid="nav-contact">İletişim</a>
          </nav>
        </div>
      </header>

      <section className="hero-section" data-testid="hero-section">
        <picture>
          <source srcSet="/hero-mobile.avif" type="image/avif" media="(max-width: 768px)" />
          <source srcSet="/hero-desktop.avif" type="image/avif" media="(min-width: 769px)" />
          <source srcSet="/hero-mobile.webp" type="image/webp" media="(max-width: 768px)" />
          <source srcSet="/hero-desktop.webp" type="image/webp" media="(min-width: 769px)" />
          <img
            src="/hero-desktop.webp"
            alt="Ankara'da işe giriş sağlık raporu için EKG ve laboratuvar tetkikleri yapan güleryüzlü sağlık personeli"
            className="hero-bg"
            width="1600"
            height="893"
            decoding="async"
            fetchPriority="high"
          />
        </picture>
        <div className="hero-overlay" aria-hidden="true"></div>

        <div className="hero-content">
          <div className="hero-city-switcher-wrap">
            <CitySwitcher
              activeSlug={activeCity ? activeCity.slug : ''}
              activeName={cityName}
              onSelect={handleSelectCity}
            />
          </div>

          <div className="hero-badge">
            <Check />
            <span>T.C. SAĞLIK BAKANLIĞI ONAYLI</span>
          </div>

          <h1 className="hero-title" data-testid="hero-title">
            <span className="hero-title-city">{cityName}</span>
            <span className="hero-title-rest"> İşe Giriş Sağlık Raporu</span>
          </h1>

          <p className="hero-subtitle">
            {personalized
              ? `${cityName}'dan Ankara'ya iş için gelenler dahil, Ostim ve Çankaya şubelerimizde raporunuz yarım saatte teslim. Uzman hekim onaylı. Tetkik listenizi iletin, fiyat teklifinizi gönderelim. Uygun fiyat garantisi. OSGB sağlık raporu, iş için tüm sağlık raporları.`
              : `Ostim ve Çankaya şubelerinde raporunuz yarım saatte teslim. Uzman hekim onaylı. Tetkik listenizi iletin, fiyat teklifinizi gönderelim. Uygun fiyat garantisi. OSGB sağlık raporu, iş için tüm sağlık raporları.`}
          </p>
          <p className="hero-tests">
            <strong>Tetkikler:</strong> Tam kan (hemogram), Akciğer grafisi, Odyometri (işitme), Solunum fonksiyon testi, EKG, Göz muayenesi, Kan grubu kartı, Tetanos aşısı, İdrar testi, ELISA testleri, portör ve diğer testler.. Yüze yakın parametre değerlendirilmektedir.
          </p>

          <div className="hero-trust-bar">
            <div className="trust-item">
              <Check />
              <span>Yarım Saatte Teslim</span>
            </div>
            <div className="trust-item">
              <Check />
              <span>Uygun Fiyat</span>
            </div>
            <div className="trust-item">
              <Check />
              <span>Bu Haftaya Özel %20 İndirim</span>
            </div>
          </div>
        </div>
      </section>

      <section className="value-props-section" aria-label="Hizmet Avantajları">
        <div className="container">
          <div className="value-props-grid">
            <article className="value-prop-card">
              <div className="value-prop-icon"><Clock /></div>
              <h2 className="value-prop-title">Yarım Saatte Teslim</h2>
              <p>Raporunuzu yarım saatte teslim alın. Ostim ve Çankaya şubelerimizde bekletmeden işlem yapılır.</p>
            </article>

            <article className="value-prop-card">
              <div className="value-prop-icon"><Shield /></div>
              <h2 className="value-prop-title">Uzman Hekim Onaylı Rapor</h2>
              <p>İşyeri hekimi imzalı, 6331 sayılı İSG Kanununa uygun, OSGB onaylı sağlık raporu. Tüm resmi ve özel kurumlar tarafından kabul edilir.</p>
            </article>

            <article className="value-prop-card">
              <div className="value-prop-icon"><CheckCircle /></div>
              <h2 className="value-prop-title">Uygun Fiyat Avantajı</h2>
              <p>Aldığınız tüm fiyatlardan daha uygun fiyat garantisi. Şeffaf fiyatlandırma, gizli ücret yok. Aradığınızda net fiyat bilgisi alın.</p>
            </article>
          </div>
        </div>
      </section>

      <Suspense fallback={null}>
        <BelowFold cityName={cityName} personalized={personalized} waHref={waHref} />
      </Suspense>

      <div className="mobile-action-buttons">
        <a
          href="tel:+905535614445"
          className="action-button call-button"
          aria-label="Hemen arayın"
          data-testid="mobile-call-btn"
        >
          <Phone />
          <span>Hemen Ara</span>
        </a>

        <a
          href={waHref}
          className="action-button whatsapp-button"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="WhatsApp ile yazın"
          data-testid="mobile-whatsapp-btn"
        >
          <WhatsApp />
          <span>WhatsApp</span>
        </a>
      </div>
    </div>
  );
}

export default App;
